'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useCart } from '@/context/cart-context';
import type { ProductVariant } from '@/lib/shopify/types';
import type { DigitisingFeeData } from '@/app/products/[handle]/product-page-client';
import type { SizeQuantityLine } from './size-quantity-selector';
import { getCustomizerCartAttributes, hasCustomDesign, type CustomDesignState } from '@/lib/customizer';
import {
  finalizeCustomizerDesign,
  type PendingArtworkFiles,
} from '@/lib/customizer-client';

interface AddToCartButtonProps {
  variant: ProductVariant | null;
  availableForSale: boolean;
  customSize?: string;
  artworkUrl?: string;
  customDesign?: CustomDesignState;
  productId: string;
  productTitle: string;
  selectedImageUrl?: string;
  selectedImageAlt?: string;
  sizeQuantityLines?: SizeQuantityLine[];
  requireSizeQuantities?: boolean;
  isCustomizerUploading?: boolean;
  pendingArtworkFiles?: PendingArtworkFiles;
  digitisingFees?: {
    front: DigitisingFeeData | null;
    back: DigitisingFeeData | null;
  };
  frontDigitising?: boolean;
  backDigitising?: boolean;
  finishAttribute?: string;
}

export default function AddToCartButton({ variant, availableForSale, customSize, artworkUrl, customDesign, productId, productTitle, selectedImageUrl, selectedImageAlt, sizeQuantityLines = [], requireSizeQuantities = false, isCustomizerUploading = false, pendingArtworkFiles, digitisingFees, frontDigitising, backDigitising, finishAttribute }: AddToCartButtonProps) {
  const [quantity, setQuantity] = useState(1);
  // Separate string state for the editable input lets the field be visually
  // empty during typing (e.g. mid-backspace) without forcing the numeric
  // quantity to fall back to 1 mid-edit. Synced on +/- and on blur.
  const [quantityInput, setQuantityInput] = useState('1');
  const [isAdding, setIsAdding] = useState(false);
  const [isFinalizingDesign, setIsFinalizingDesign] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showBlankDesignWarning, setShowBlankDesignWarning] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');
  const { addItem, addItems, isLoading: isCartLoading } = useCart();

  const sizeRequired = customSize !== undefined && !customSize;
  const hasSizeQuantityLines = sizeQuantityLines.length > 0;
  const sizeQuantitiesRequired = requireSizeQuantities && !hasSizeQuantityLines;

  const addToCart = async () => {
    if (!variant || !availableForSale || sizeRequired || sizeQuantitiesRequired || isCustomizerUploading || isFinalizingDesign || isCartLoading) return;

    const shouldFinalizeDesign = pendingArtworkFiles !== undefined;
    setIsAdding(true);
    setIsFinalizingDesign(shouldFinalizeDesign);
    setFinalizeError('');
    try {
      const finalized = !shouldFinalizeDesign
        ? { design: customDesign, previewUrl: undefined }
        : await finalizeCustomizerDesign({
            design: customDesign,
            pendingArtworkFiles,
            productTitle,
            selectedImageUrl,
          });

      const attributes: { key: string; value: string }[] = [];
      if (finishAttribute) attributes.push({ key: 'Finish', value: finishAttribute });
      if (customSize) attributes.push({ key: 'Size', value: customSize });
      if (artworkUrl) attributes.push({ key: 'Artwork', value: artworkUrl });
      attributes.push(...getCustomizerCartAttributes(finalized.design));
      if (finalized.previewUrl) attributes.push({ key: 'Design preview', value: finalized.previewUrl });
      // Pin the resolved colour-matched image to the cart line so the cart drawer
      // shows the variant the customer actually picked, not the product's default
      // featuredImage (which is the primary/Black variant).
      if (selectedImageUrl) {
        attributes.push({ key: '_image', value: selectedImageUrl });
        if (selectedImageAlt) attributes.push({ key: '_imageAlt', value: selectedImageAlt });
      }
      // Track which sides this variant wants embroidered. The cart drawer uses
      // these flags to show each fee under EVERY variant of the product that
      // needs that side (not just the variant that triggered the fee), and the
      // cascade uses them to remove the fee only when no remaining variant
      // still needs that side.
      if (frontDigitising) attributes.push({ key: '_embroideryFront', value: 'true' });
      if (backDigitising) attributes.push({ key: '_embroideryBack', value: 'true' });
      const garmentLines = hasSizeQuantityLines
        ? sizeQuantityLines.map((line) => ({
            variantId: line.variant.id,
            quantity: line.quantity,
            attributes: attributes.length > 0 ? attributes : undefined,
          }))
        : [{ variantId: variant.id, quantity, attributes: attributes.length > 0 ? attributes : undefined }];
      const primaryGarmentVariantId = garmentLines[0]?.variantId || variant.id;

      // Build lines array: garment + any digitising fees
      const needsFees = (frontDigitising && digitisingFees?.front) || (backDigitising && digitisingFees?.back);

      if (needsFees) {
        // Two link attributes serve different purposes:
        //   _linkedProduct — used by dedup + cascade (one fee per product).
        //   _linkedVariant — used by the cart drawer to show each fee under
        //     the variant that actually triggered it (e.g. L picks front, M
        //     picks back → front fee under L, back fee under M).
        const feeAttributes = [
          { key: 'For', value: productTitle },
          { key: '_linkedProduct', value: productId },
          { key: '_linkedVariant', value: primaryGarmentVariantId },
        ];
        const lines: { variantId: string; quantity: number; attributes?: { key: string; value: string }[] }[] = [...garmentLines];
        if (frontDigitising && digitisingFees?.front) {
          lines.push({ variantId: digitisingFees.front.variantId, quantity: 1, attributes: feeAttributes });
        }
        if (backDigitising && digitisingFees?.back) {
          lines.push({ variantId: digitisingFees.back.variantId, quantity: 1, attributes: feeAttributes });
        }
        await addItems(lines);
      } else if (hasSizeQuantityLines) {
        await addItems(garmentLines);
      } else {
        await addItem(variant.id, quantity, attributes.length > 0 ? attributes : undefined);
      }

      setShowSuccess(true);
      setShowBlankDesignWarning(false);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      setFinalizeError(error instanceof Error ? error.message : 'Could not prepare your design. Please try again.');
    } finally {
      setIsFinalizingDesign(false);
      setIsAdding(false);
    }
  };

  const handleAddToCart = async () => {
    if (customDesign && !hasCustomDesign(customDesign) && !showBlankDesignWarning) {
      setShowBlankDesignWarning(true);
      document.getElementById('product-customizer')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }

    await addToCart();
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      const next = quantity - 1;
      setQuantity(next);
      setQuantityInput(String(next));
    }
  };

  const incrementQuantity = () => {
    const next = quantity + 1;
    setQuantity(next);
    setQuantityInput(String(next));
  };

  const isDisabled = !variant || !availableForSale || isAdding || isFinalizingDesign || sizeRequired || sizeQuantitiesRequired || isCustomizerUploading || isCartLoading;

  return (
    <div className="space-y-4">
      {/* Quantity Selector */}
      {!requireSizeQuantities && (
      <div>
        <span className="block text-sm font-medium uppercase tracking-wider mb-3">Quantity</span>
        <div className="flex items-center gap-0">
          <button
            onClick={decrementQuantity}
            disabled={quantity <= 1}
            className="w-12 h-12 flex items-center justify-center border border-[var(--color-border)] hover:bg-[var(--color-off-white)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Decrease quantity"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
            </svg>
          </button>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={quantityInput}
            onChange={(e) => {
              // Allow empty/intermediate typing freely. Only commit a valid
              // integer ≥ 1 to `quantity`; the visual `quantityInput` mirrors
              // exactly what the user sees, so backspace doesn't snap back.
              const raw = e.target.value;
              if (raw === '' || /^\d+$/.test(raw)) {
                setQuantityInput(raw);
                if (raw !== '') {
                  const v = parseInt(raw, 10);
                  if (v >= 1) setQuantity(v);
                }
              }
            }}
            onBlur={() => {
              // Final clamp: if the user left the field empty or at 0,
              // restore both states to a valid 1.
              const v = parseInt(quantityInput, 10);
              if (isNaN(v) || v < 1) {
                setQuantity(1);
                setQuantityInput('1');
              } else {
                setQuantityInput(String(v));
              }
            }}
            onFocus={(e) => {
              // Defer so the click's mouseup doesn't reset the cursor
              // position over our selection. Without the timeout the
              // selection is wiped before the user can type.
              const target = e.currentTarget;
              setTimeout(() => target.select(), 0);
            }}
            aria-label="Quantity"
            className="w-16 h-12 text-center border-t border-b border-[var(--color-border)] text-sm font-medium focus:outline-none focus:border-black bg-white"
          />
          <button
            onClick={incrementQuantity}
            className="w-12 h-12 flex items-center justify-center border border-[var(--color-border)] hover:bg-[var(--color-off-white)] transition-colors"
            aria-label="Increase quantity"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
        </div>
      </div>
      )}

      {/* Add to Cart Button */}
      <motion.button
        onClick={handleAddToCart}
        disabled={isDisabled}
        whileTap={{ scale: 0.98 }}
        className={`relative w-full py-4 text-sm font-semibold uppercase tracking-wider transition-all ${
          isDisabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : showSuccess
            ? 'bg-green-600 text-white'
            : 'bg-brand-blue text-white hover:bg-brand-blue-hover'
        }`}
      >
        {isFinalizingDesign ? (
          'Preparing Design...'
        ) : isAdding ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Adding...
          </span>
        ) : isCartLoading ? (
          'Preparing Cart...'
        ) : isCustomizerUploading ? (
          'Uploading Artwork...'
        ) : showSuccess ? (
          <span className="flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            Added to Cart
          </span>
        ) : !availableForSale ? (
          'Out of Stock'
        ) : sizeRequired ? (
          'Select a Size'
        ) : sizeQuantitiesRequired ? (
          'Select Quantities'
        ) : (
          'Add to Cart'
        )}
      </motion.button>

      {finalizeError && (
        <p className="text-sm text-red-600" role="alert">
          {finalizeError}
        </p>
      )}

      {showBlankDesignWarning && (
        <div className="border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-black">No design has been added yet.</p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Add artwork, text, or instructions before checkout, or continue with the current product options.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => document.getElementById('product-customizer')?.scrollIntoView({ block: 'center', behavior: 'smooth' })}
              className="border border-brand-blue bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-brand-blue hover:bg-[var(--color-off-white)]"
            >
              Back to design
            </button>
            <button
              type="button"
              onClick={addToCart}
              disabled={isAdding || sizeQuantitiesRequired || isCustomizerUploading || isCartLoading}
              className="bg-brand-blue px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-brand-blue-hover disabled:opacity-60"
            >
              Continue anyway
            </button>
          </div>
        </div>
      )}

      {/* Trust Badges */}
      <div className="space-y-2 pt-2">
        <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-green-600">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          Standard UK delivery £6.99 · Next day £9.99
        </div>
        <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-green-600">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          Custom printing & embroidery available (one-time digitising fee applies for embroidery)
        </div>
      </div>
    </div>
  );
}
